import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../config/axios';

const formatPrice = (price) =>
  Number(price || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/products/${id}`);
        setProduct(data);
      } catch (err) {
        setError('This listing could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    const fetchCurrentUser = async () => {
      if (!localStorage.getItem('authToken')) return;
      try {
        const { data } = await axiosInstance.get('/api/users/profile');
        setCurrentUser(data);
      } catch (err) {
        setCurrentUser(null);
      }
    };

    fetchProduct();
    fetchCurrentUser();
  }, [id]);

  const addToCart = async () => {
    if (!localStorage.getItem('authToken')) {
      navigate('/login');
      return;
    }

    setError('');
    setSuccess('');

    try {
      await axiosInstance.post('/api/cart/add', { productId: product._id, quantity });
      setProduct((prev) => ({ ...prev, quantity: prev.quantity - quantity }));
      setQuantity(1);
      setSuccess('Added to cart. You can keep shopping or review your cart.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add this item to your cart.');
    }
  };

  const showPreviousImage = () => {
    setActiveImage((current) => (current === 0 ? images.length - 1 : current - 1));
  };

  const showNextImage = () => {
    setActiveImage((current) => (current === images.length - 1 ? 0 : current + 1));
  };

  if (loading) return <div className="page-shell"><div className="state-card">Loading listing...</div></div>;
  if (!product) return <div className="page-shell"><div className="state-card">{error || 'Listing not found.'}</div></div>;

  const isProductOwner = currentUser && product.addedBy?._id === currentUser._id;
  const images = product.images?.length ? product.images : [];
  const maxQuantity = Math.max(product.quantity, 1);

  return (
    <div className="page-shell">
      <Link className="back-link" to="/">Back to marketplace</Link>
      <section className="details-layout">
        <div className="gallery-panel">
          <div className="main-product-image">
            <button
              type="button"
              className="enlarge-image-button"
              onClick={() => setEnlargedImage(images[activeImage])}
              aria-label="Enlarge product image"
            >
              <img src={images[activeImage]} alt={product.name} />
            </button>
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="carousel-control previous"
                  onClick={showPreviousImage}
                  aria-label="Show previous product image"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="carousel-control next"
                  onClick={showNextImage}
                  aria-label="Show next product image"
                >
                  ›
                </button>
                <span className="carousel-count">
                  {activeImage + 1} / {images.length}
                </span>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="thumbnail-row">
              {images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={activeImage === index ? 'active' : ''}
                  onClick={() => setActiveImage(index)}
                  aria-label={`View product image ${index + 1}`}
                >
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <article className="product-info-panel">
          <span className={`stock-badge inline ${product.quantity === 0 ? 'sold-out' : ''}`}>
            {product.quantity > 0 ? `${product.quantity} available` : 'Sold out'}
          </span>
          <h1>{product.name}</h1>
          <div className="detail-price">{formatPrice(product.price)}</div>
          <p className="detail-description">{product.description}</p>

          <div className="seller-strip">
            <span>Sold by</span>
            <strong>{product.addedBy?.username || 'Marketplace seller'}</strong>
          </div>

          {error && <div className="notice error">{error}</div>}
          {success && <div className="notice success">{success}</div>}

          {isProductOwner ? (
            <button className="button button-primary" onClick={() => navigate(`/edit-product/${product._id}`)}>
              Edit Listing
            </button>
          ) : product.quantity > 0 ? (
            <div className="buy-box">
              <label>
                Quantity
                <input
                  type="number"
                  value={quantity}
                  min="1"
                  max={maxQuantity}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setQuantity(Math.min(maxQuantity, Math.max(1, value || 1)));
                  }}
                />
              </label>
              <button className="button button-primary" onClick={addToCart}>
                Add to Cart
              </button>
            </div>
          ) : (
            <button className="button button-disabled" disabled>
              Sold Out
            </button>
          )}

          <div className="purchase-notes">
            <div>
              <strong>Checkout</strong>
              <span>Review quantities before removing or buying.</span>
            </div>
            <div>
              <strong>Seller managed</strong>
              <span>Listings update stock automatically when added to cart.</span>
            </div>
          </div>
        </article>
      </section>

      {enlargedImage && (
        <div className="image-lightbox" role="dialog" aria-modal="true" aria-label="Enlarged product image">
          <button
            type="button"
            className="lightbox-backdrop"
            aria-label="Close enlarged product image"
            onClick={() => setEnlargedImage(null)}
          ></button>
          <div className="lightbox-content">
            <button
              type="button"
              className="lightbox-close"
              aria-label="Close enlarged product image"
              onClick={() => setEnlargedImage(null)}
            >
              ×
            </button>
            <img src={enlargedImage} alt={`${product.name} enlarged`} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetailsPage;
